import { onMounted, onUnmounted } from 'vue';
import type { CaseHit, HitRecord, HitVerdict } from './caseDetail';

export interface ShortcutsConfig {
  hits: () => CaseHit[];
  selectedHitId: () => string;
  selectHit: (id: string) => void;
  records: () => HitRecord[];
  focusedRecordIndex: () => number;
  setFocusedRecordIndex: (index: number) => void;
  selectVerdict: (hitId: string, verdict: HitVerdict) => void;
  cycleRecordStatus: (recordId: string) => void;
  openReader: (recordId: string) => void;
  setToolByIndex: (index: number) => void;
  openNoteComposer: () => void;
  // Escape while typing blurs the field *and* drops out of composing,
  // matching v4's own Escape-while-typing behavior — not just this
  // hook's generic overlay-close path, since the note composer isn't an
  // "overlay" (it lives inline in the sticky verdict bar).
  cancelNoteComposer: () => void;
  togglePane: () => void;
  switchPaneTab: () => void;
  openShortcuts: () => void;
  save: () => void;
  submit: () => void;
  kbEnabled: () => boolean;
  isOverlayOpen: () => boolean;
  closeOverlay: () => void;
  openDocumentViewer: () => void;
  isDocumentViewerOpen: () => boolean;
  stepDocumentViewer: (delta: number) => void;
  rotateDocumentViewer: (delta: number) => void;
  // The full-pane record reviewing mode (Stage 6d) deliberately does NOT
  // join isOverlayOpen/closeOverlay — v4's own handler never gates T/F/U,
  // N, O, C, 1–6, G or R behind `view === 'review'`, only Escape and hit
  // navigation treat it specially (see the ArrowLeft/ArrowRight cases
  // below, which exit review on a hit change, matching v4's moveHit).
  isReviewing: () => boolean;
  closeReview: () => void;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable;
}

// Matches the `lg:` Tailwind breakpoint the responsive layout itself uses
// (v4's own desktop cutoff) — shortcuts are inert below it, same as typing
// in a field or an overlay being open, so a phone-width viewport never
// intercepts a key a mobile browser or IME needs.
function isDesktopViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
}

// The pure event handler, exported separately from the window-listener
// wiring below so it can be unit tested without dispatching real DOM events.
export function createKeydownHandler(config: ShortcutsConfig) {
  return function handleKeydown(e: KeyboardEvent) {
    const meta = e.metaKey || e.ctrlKey;

    // Modifier-based and always real product actions (not the letter-key
    // shortcuts the on/off switch exists to silence for a screen reader or
    // IME), so these stay live regardless of kbEnabled/viewport/typing.
    if (meta && e.key.toLowerCase() === 's') {
      e.preventDefault();
      config.save();
      return;
    }
    if (meta && e.key === 'Enter') {
      e.preventDefault();
      config.submit();
      return;
    }

    if (meta || e.altKey) return;

    // The document preview works even with the shortcut switch off and
    // below the desktop breakpoint, so Escape always gets a reviewer out
    // of it and rotate/nav keys always work while it's open — everything
    // else stays gated behind kbEnabled/isDesktopViewport below.
    if (config.isDocumentViewerOpen()) {
      if (e.key === 'Escape') {
        e.preventDefault();
        config.closeOverlay();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        config.stepDocumentViewer(-1);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        config.stepDocumentViewer(1);
        return;
      }
      if (e.key === 'r' || e.key === ']') {
        e.preventDefault();
        config.rotateDocumentViewer(90);
        return;
      }
      if (e.key === 'R' || e.key === '[') {
        e.preventDefault();
        config.rotateDocumentViewer(-90);
        return;
      }
      return;
    }

    if (!config.kbEnabled() || !isDesktopViewport()) return;

    const typing = isTypingTarget(e.target);

    if (e.key === 'Escape') {
      if (config.isOverlayOpen()) {
        config.closeOverlay();
        return;
      }
      if (config.isReviewing()) {
        config.closeReview();
        return;
      }
      if (typing) {
        (e.target as HTMLElement).blur();
        config.cancelNoteComposer();
      }
      return;
    }

    if (config.isOverlayOpen() || typing) return;

    const hits = config.hits();
    const hitIndex = hits.findIndex((hit) => hit.id === config.selectedHitId());
    const records = config.records();
    const focusedRecord = records[config.focusedRecordIndex()];

    switch (e.key) {
      case 'ArrowLeft':
        if (hitIndex > 0) {
          config.closeReview();
          config.selectHit(hits[hitIndex - 1].id);
        }
        break;
      case 'ArrowRight':
        if (hitIndex >= 0 && hitIndex < hits.length - 1) {
          config.closeReview();
          config.selectHit(hits[hitIndex + 1].id);
        }
        break;
      case 'ArrowUp':
      case 'k':
      case 'K':
        e.preventDefault();
        if (records.length) {
          config.setFocusedRecordIndex(Math.max(0, config.focusedRecordIndex() - 1));
        }
        break;
      case 'ArrowDown':
      case 'j':
      case 'J':
        e.preventDefault();
        if (records.length) {
          config.setFocusedRecordIndex(
            Math.min(records.length - 1, config.focusedRecordIndex() + 1)
          );
        }
        break;
      case '?':
        e.preventDefault();
        config.openShortcuts();
        break;
      case 't':
      case 'T':
        config.selectVerdict(config.selectedHitId(), 'true_positive');
        break;
      case 'f':
      case 'F':
        config.selectVerdict(config.selectedHitId(), 'false_positive');
        break;
      case 'u':
      case 'U':
        config.selectVerdict(config.selectedHitId(), 'unknown');
        break;
      case 'n':
      case 'N':
        if (focusedRecord) config.cycleRecordStatus(focusedRecord.id);
        break;
      case 'Enter':
        // Every record kind can be opened in the reader (it shows a fields
        // table when there's no body/snippet to show instead), so this no
        // longer needs a body/snippet gate or a URL fallback — "O" already
        // covers opening the source directly.
        if (focusedRecord) {
          e.preventDefault();
          config.openReader(focusedRecord.id);
        }
        break;
      case 'o':
      case 'O':
        if (focusedRecord?.url) window.open(focusedRecord.url, '_blank', 'noopener');
        break;
      case 'c':
      case 'C':
        // Prevented because openNoteComposer moves focus into the note
        // textarea on the next tick — without this, Chrome can still
        // deliver this same keystroke's character-insertion step to that
        // now-focused textarea, typing a stray "c" into the note the
        // instant it opens.
        e.preventDefault();
        config.openNoteComposer();
        break;
      case 'g':
      case 'G':
        config.togglePane();
        break;
      case 'r':
      case 'R':
        config.switchPaneTab();
        break;
      case 'd':
      case 'D':
        config.openDocumentViewer();
        break;
      default: {
        const asNumber = Number(e.key);
        if (Number.isInteger(asNumber) && asNumber >= 1 && asNumber <= 6) {
          config.setToolByIndex(asNumber - 1);
        }
      }
    }
  };
}

export function useAmlWorkbenchShortcuts(config: ShortcutsConfig) {
  const handler = createKeydownHandler(config);
  onMounted(() => window.addEventListener('keydown', handler));
  onUnmounted(() => window.removeEventListener('keydown', handler));
}
